import { plural, t } from '@lingui/core/macro';
import { isPresent } from '@luminovo/commons';
import {
    colorSystem,
    createDateValidator,
    fieldConverterDate,
    Flexbox,
    InputAdornment,
    SearchInput,
} from '@luminovo/design-system';
import { formatToIso8601Date } from '@luminovo/frontend-utils';
import React from 'react';

import { Column, RowData } from '../tanstackTypes';
import { DateRange, FilterValue, getFilterConfig, RelativeDateFilterValue, SupportedFilterFn } from '../type';
import { createFilterValueUpdater, isFilterValue } from './createFilterValueUpdater';

function parseDateValue(value: string | undefined): string | null {
    if (!isPresent(value) || value === '' || (typeof value === 'string' && value.trim() === '')) {
        return null;
    }

    const parsedValue = formatToIso8601Date(value, { ifAbsent: '' });
    return Boolean(parsedValue) ? parsedValue : null;
}

export function FilterInDateRange<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}): JSX.Element | null {
    const filterConfig = getFilterConfig(column, 'date');

    if (!isPresent(filterConfig)) {
        return null;
    }

    const handleMinChange = (value: string | undefined) => {
        const dateValue = parseDateValue(value);
        column.setFilterValue(
            createFilterValueUpdater((old: FilterValue<DateRange> | undefined) => {
                const oldDateRange = old?.value as DateRange | undefined;
                const max = oldDateRange?.[1] ?? null;
                if (!isPresent(dateValue) && !isPresent(max)) {
                    return undefined;
                }

                return [dateValue, max];
            }, filterConfig.defaultFilterFn),
        );
    };

    const handleMaxChange = (value: string | undefined) => {
        const dateValue = parseDateValue(value);
        column.setFilterValue(
            createFilterValueUpdater((old: FilterValue<DateRange> | undefined) => {
                const oldDateRange = old?.value as DateRange | undefined;
                const min = oldDateRange?.[0] ?? null;
                if (!isPresent(dateValue) && !isPresent(min)) {
                    return undefined;
                }
                return [min, dateValue];
            }, filterConfig.defaultFilterFn),
        );
    };
    const filterValue = column.getFilterValue();
    const dateRangeValue =
        isFilterValue<DateRange>(filterValue) && filterValue.filterFn === SupportedFilterFn.inDateRange
            ? (filterValue.value as DateRange)
            : undefined;
    const valueMin = typeof dateRangeValue?.[0] === 'string' ? dateRangeValue[0] : undefined;
    const valueMax = typeof dateRangeValue?.[1] === 'string' ? dateRangeValue[1] : undefined;

    return (
        <Flexbox gap={4}>
            <SearchInput
                value={valueMin}
                onChange={handleMinChange}
                debounceWait={50}
                onKeyEnter={onClose}
                onClear={() => handleMinChange(undefined)}
                style={{ backgroundColor: colorSystem.neutral.white, width: '168px' }}
                formatter={{
                    converter: fieldConverterDate,
                    validation: createDateValidator({ required: false }),
                }}
                slotProps={{
                    input: {
                        type: 'date',
                        startAdornment: <InputAdornment position="start">{t`Min`}</InputAdornment>,
                    },
                }}
            />
            <SearchInput
                value={valueMax}
                onChange={handleMaxChange}
                debounceWait={50}
                onKeyEnter={onClose}
                onClear={() => handleMaxChange(undefined)}
                style={{ backgroundColor: colorSystem.neutral.white, width: '168px' }}
                formatter={{
                    converter: fieldConverterDate,
                    validation: createDateValidator({ required: false }),
                }}
                slotProps={{
                    input: {
                        type: 'date',
                        startAdornment: <InputAdornment position="start">{t`Max`}</InputAdornment>,
                    },
                }}
            />
        </Flexbox>
    );
}

export function formatRelativeDateFilterLabel(days: number): string {
    if (days % 365 === 0) {
        const years = days / 365;
        return plural(years, { one: '1 year', other: '# years' });
    }
    if (days % 7 === 0) {
        const weeks = days / 7;
        return plural(weeks, { one: '1 week', other: '# weeks' });
    }
    return plural(days, { one: '# day', other: '# days' });
}

export function FilterInRelativeDateRange<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}): JSX.Element | null {
    const filterValue = column.getFilterValue();
    const activeFilterFn =
        isFilterValue<RelativeDateFilterValue>(filterValue) &&
        (filterValue.filterFn === SupportedFilterFn.isInLastDays ||
            filterValue.filterFn === SupportedFilterFn.isOlderThanDays)
            ? filterValue.filterFn
            : SupportedFilterFn.isInLastDays;

    const currentDays: number | null =
        isFilterValue<RelativeDateFilterValue>(filterValue) &&
        (filterValue.filterFn === SupportedFilterFn.isInLastDays ||
            filterValue.filterFn === SupportedFilterFn.isOlderThanDays) &&
        typeof filterValue.value === 'number'
            ? filterValue.value
            : null;

    const [inputValue, setInputValue] = React.useState<string>(currentDays !== null ? String(currentDays) : '');

    // Picks up external changes to the filter, such as "Clear filter".
    React.useEffect(() => {
        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setInputValue(currentDays !== null ? String(currentDays) : '');
    }, [currentDays]);

    const filterConfig = getFilterConfig(column, 'date');

    if (!isPresent(filterConfig)) {
        return null;
    }

    // Keeps the filter function so the dropdown stays on it; a `null` value matches all rows.
    const clearValue = () => {
        setInputValue('');
        column.setFilterValue({ filterFn: activeFilterFn, value: null });
    };

    const handleInputChange = (value: string | undefined) => {
        if (!isPresent(value) || value === '') {
            clearValue();
            return;
        }
        setInputValue(value);
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed >= 0 && parsed <= 9999) {
            column.setFilterValue(createFilterValueUpdater(parsed, activeFilterFn));
        }
    };

    return (
        <SearchInput
            value={inputValue}
            onChange={handleInputChange}
            placeholder={t`Number of days`}
            debounceWait={50}
            onKeyEnter={onClose}
            onClear={clearValue}
            style={{ backgroundColor: colorSystem.neutral.white, width: '168px' }}
            slotProps={{
                input: {
                    type: 'number',
                    endAdornment: <InputAdornment position="end">{t`days`}</InputAdornment>,
                },
                htmlInput: { min: 0, max: 9999, step: 1 },
            }}
        />
    );
}
